import { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, ExternalLink, Clock, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import SectionHeading from '../components/SectionHeading';

// Yaqeen Institute articles
const yaqeenArticles = [
  {
    id: '1',
    title: 'The Meaning of La Ilaha Illa Allah: An Annotated Translation',
    excerpt: 'A profound exploration of the Islamic testimony of faith and its deeper meanings through Imam al-Zarkashi\'s treatise.',
    category: 'Faith',
    author: 'Sh. Yousef Wahb',
    readTime: '76 mins',
    image_url: 'https://i.yaqeeninstitute.org/?src=2025/12/256_Rectangle_4000x2250-scaled.jpg&w=800',
    url: 'https://yaqeeninstitute.ca/read/paper/the-meaning-of-la-ilaha-illa-allah-an-annotated-translation-of-imam-al-zarkashis-treatise-on-the-islamic-testimony-of-faith',
  },
  {
    id: '2',
    title: 'Marriage and Gender Roles in Islam: Beyond Rights and Duties',
    excerpt: 'Over the past two centuries, Muslim societies remained remarkably resilient in one vital domain: the family.',
    category: 'Family & Community',
    author: 'Dr. Tesneem Alkiek',
    readTime: '37 mins',
    image_url: 'https://i.yaqeeninstitute.org/?src=2025/10/IMG_2157-scaled.jpeg&w=800',
    url: 'https://yaqeeninstitute.ca/read/paper/marriage-and-gender-roles-in-islam-beyond-rights-and-duties',
  },
  {
    id: '3',
    title: 'Virtues of Surah al-Baqara: Healing and Protection',
    excerpt: 'Discover the virtues and benefits of the Qur\'an\'s longest chapter, including healing and spiritual protection.',
    category: 'Faith',
    author: 'Sh. Suleiman Hani',
    readTime: '38 mins',
    image_url: 'https://i.yaqeeninstitute.org/?src=2025/08/IMG_2029-scaled.jpeg&w=800',
    url: 'https://yaqeeninstitute.ca/read/paper/virtues-of-surah-al-baqara-healing-and-protection-in-the-qurans-longest-chapter',
  },
  {
    id: '4',
    title: 'Surah al-Fatiha\'s Benefits and Virtues',
    excerpt: 'The greatest surah in the Qur\'an, the foundation of Islam, a healing for illnesses, and a comfort for worries.',
    category: 'Faith',
    author: 'Sh. Suleiman Hani',
    readTime: '28 mins',
    image_url: 'https://i.yaqeeninstitute.org/?src=2025/05/251_Rectangle_4000x2250-scaled.jpg&w=800',
    url: 'https://yaqeeninstitute.ca/read/paper/surah-al-fatihas-benefits-and-virtues-the-greatest-surah-in-the-quran',
  },
  {
    id: '5',
    title: 'The Art of Gratitude: Qur\'anic Themes on Shukr',
    excerpt: 'Learn how to express shukr in your own life and reflect on how the Qur\'an offers comprehensive guidance.',
    category: 'Psychology & Mental Health',
    author: 'Dr. Tamer Desouky',
    readTime: '34 mins',
    image_url: 'https://i.yaqeeninstitute.org/?src=2022/04/209_Rectangle_4000x2250-scaled.jpg&w=800',
    url: 'https://yaqeeninstitute.ca/read/paper/the-art-of-gratitude-quranic-themes-on-shukr',
  },
  {
    id: '6',
    title: 'The Meaning of Allah\'s Name Al-Jabbar',
    excerpt: 'The Compeller and Mender of Broken Hearts - understanding one of Allah\'s beautiful names.',
    category: 'Faith',
    author: 'Sh. Mohammad Elshinawy',
    readTime: '41 mins',
    image_url: 'https://i.yaqeeninstitute.org/?src=2025/03/IMG_1059-scaled.jpeg&w=800',
    url: 'https://yaqeeninstitute.ca/read/paper/the-meaning-of-allahs-name-al-jabbar-the-compeller-and-mender-of-broken-hearts',
  },
  {
    id: '7',
    title: 'Gaza\'s Rise: Inspiring the Muslim Ummah\'s Collective Consciousness',
    excerpt: 'How the events in Gaza have inspired increased religiosity and collective consciousness among Muslims worldwide.',
    category: 'Faith',
    author: 'Dr. Osman Umarji',
    readTime: '9 mins',
    image_url: 'https://i.yaqeeninstitute.org/?src=2020/08/162_BlackAndMuslim_HEROImage_.jpg&w=800',
    url: 'https://yaqeeninstitute.ca/read/blog/gazas-rise-inspiring-the-muslim-ummahs-collective-consciousness-and-religiosity',
  },
  {
    id: '8',
    title: 'Who Are the Chosen Ones? The Qur\'an\'s Correction of the Bible',
    excerpt: 'Understanding the exact meaning of the chosenness of the Israelites through Qur\'anic clarification.',
    category: 'Faith',
    author: 'Dr. Louay Fatoohi',
    readTime: '65 mins',
    image_url: 'https://i.yaqeeninstitute.org/?src=2024/12/V4_PaperSquad_Children.of_.Israel_1920x1080.png&w=800',
    url: 'https://yaqeeninstitute.ca/read/paper/who-are-the-chosen-ones-the-qurans-correction-of-the-bible-on-the-election-of-the-children-of-israel',
  },
  {
    id: '9',
    title: 'Islamic Parenting Strategies',
    excerpt: 'How to help children feel safe in their attachment to God and parents through Islamic parenting.',
    category: 'Parenting',
    author: 'Sarah Sultan',
    readTime: '56 mins',
    image_url: 'https://i.yaqeeninstitute.org/?src=2020/05/155_MarriageandSocialIsolation_HEROImage_.jpg&w=800',
    url: 'https://yaqeeninstitute.ca/read/paper/islamic-parenting-strategies-how-to-help-children-feel-safe-in-their-attachment-to-god-and-parents',
  },
];

const NewsPage = () => {
  const [filter, setFilter] = useState('All');
  
  const categories = ['All', 'Faith', 'Family & Community', 'Psychology & Mental Health', 'Parenting'];
  
  const filteredArticles = filter === 'All' 
    ? yaqeenArticles 
    : yaqeenArticles.filter(article => article.category === filter);

  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 lg:py-24 hero-gradient islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Islamic Knowledge
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground font-['Outfit'] leading-tight mb-6">
              News & <span className="text-gradient">Articles</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Explore research papers and articles from Yaqeen Institute for Islamic Research. 
              Strengthen your faith with scholarly insights.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-6 bg-card/50 border-b border-border sticky top-20 z-30 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground mr-2">Filter:</span>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === cat 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-accent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-24 lg:py-32" data-testid="news-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article, index) => (
              <motion.article
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
                data-testid={`article-${article.id}`}
              >
                <a 
                  href={article.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <div className="bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-all duration-300">
                    {article.image_url && (
                      <div className="aspect-video overflow-hidden relative">
                        <img 
                          src={article.image_url} 
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500"
                        />
                        <div className="absolute top-3 right-3">
                          <div className="p-2 rounded-full bg-black/50 backdrop-blur-sm">
                            <ExternalLink className="h-4 w-4 text-white" />
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="p-6 space-y-4">
                      <div className="flex items-center gap-3 text-sm">
                        <span className="px-3 py-1 rounded-full bg-primary/10 text-primary">
                          {article.category}
                        </span>
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {article.readTime}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold font-['Outfit'] text-foreground group-hover:text-primary transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-muted-foreground text-sm line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between pt-2">
                        <span className="text-sm text-muted-foreground">{article.author}</span>
                        <span className="text-primary text-sm font-medium flex items-center gap-1">
                          Read on Yaqeen
                          <ExternalLink className="h-3 w-3" />
                        </span>
                      </div>
                    </div>
                  </div>
                </a>
              </motion.article>
            ))}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No articles found for this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Yaqeen CTA */}
      <section className="py-24 lg:py-32 bg-card islamic-pattern" data-testid="yaqeen-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading
            badge="Explore More"
            title="Visit Yaqeen Institute"
            description="Access hundreds of research papers, blogs, and resources to strengthen your faith and understanding of Islam."
          />
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://yaqeeninstitute.ca/read"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-8">
                <BookOpen className="mr-2 h-5 w-5" />
                Explore Yaqeen Institute
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsPage;
